import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react';
import { WorkspaceRole } from '@mg/shared';
import type {
  ManageableWorkspace,
  ManagedMember,
  RegisteredEntraGroup,
  SeatUsageDto,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { useIsWorkspaceAdmin, useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { useTenantKey } from '../../lib/useTenantKey';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FormField, Input, Select } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { useDialogs } from '../../components/ui/DialogProvider';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useSaveHold } from '../../components/settings/footerSave';
import { CopyableUrl } from '../settings/integrationUi';

const WORKSPACE_ROLES = Object.values(WorkspaceRole);

/**
 * Seats in use across the whole installation.
 *
 * Install-level, not per-workspace: one person in three Brand Workspaces is one seat, and the
 * count has to be the same number Billing charges for. Kept in its own light endpoint so this card
 * does not drag the billing page's Stripe round-trips in with it.
 */
function useSeatUsage() {
  return useQuery({
    queryKey: ['seat-usage'],
    queryFn: ({ signal }) => api.getSeatUsage(signal),
  });
}

/**
 * Everyone who currently holds a seat, INSTALL-WIDE.
 *
 * Not the managed-members list: that is scoped to the workspaces the caller administers, so a
 * Workspace Admin cannot see a colleague who belongs only to someone else's workspace. Judging
 * "would this person take a new seat?" against that partial view told them a seat-neutral add was
 * impossible and advised removing a colleague to make room that was never needed. The seat roster
 * is the same predicate the count comes from, and it is readable by every member.
 */
function useSeatHolders() {
  const q = useQuery({
    queryKey: ['billing-members'],
    queryFn: ({ signal }) => api.getBillingMembers(signal),
  });
  return {
    emails: new Set((q.data ?? []).map((m) => m.email.toLowerCase())),
    // Until it loads, assume nothing — the server is the authority and refuses accurately.
    loaded: q.isSuccess,
  };
}

/** Seats left before the cap bites, or null when there is no cap. */
function seatsRemaining(usage: SeatUsageDto | undefined): number | null {
  if (!usage || usage.cap === null) return null;
  return Math.max(0, usage.cap - usage.members);
}

/** "5 of 10 seats used" — the number an admin needs BEFORE they try to add someone. */
function SeatCounter({ usage }: { usage: SeatUsageDto }) {
  const remaining = seatsRemaining(usage);
  if (remaining === null) {
    return (
      <span className="text-sm text-ink-muted">
        {usage.members} {usage.members === 1 ? 'seat' : 'seats'} in use
      </span>
    );
  }
  const full = remaining <= 0;
  return (
    <span className="flex flex-wrap items-center gap-2 text-sm">
      <Badge tone={full ? 'red' : remaining === 1 ? 'amber' : 'neutral'} size="sm">
        {usage.members} of {usage.cap} seats used
      </Badge>
      <span className={full ? 'text-red-600 dark:text-error-dark' : 'text-ink-muted'}>
        {full ? 'Cap reached' : `${remaining} left`}
      </span>
    </span>
  );
}

/**
 * Human label for a per-workspace role. Workspaces are surfaced as "Brand Workspaces" throughout
 * this section, so WORKSPACE_ADMIN reads as "Brand Workspace Admin".
 */
function workspaceRoleLabel(role: WorkspaceRole): string {
  return role === WorkspaceRole.WORKSPACE_ADMIN ? 'Brand Workspace Admin' : 'Member';
}

/** Single-select of the per-workspace access role (Brand Workspace Admin | Member). */
function RoleSelect({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: WorkspaceRole;
  disabled?: boolean;
  onChange: (role: WorkspaceRole) => void;
}) {
  return (
    <Select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as WorkspaceRole)}
    >
      {WORKSPACE_ROLES.map((r) => (
        <option key={r} value={r}>
          {workspaceRoleLabel(r)}
        </option>
      ))}
    </Select>
  );
}

/**
 * Per-user inline editor of workspace access, spanning every workspace the caller manages.
 * Each row is a checkbox (is the user a member of this workspace?) plus a role select. Checking
 * a box grants access with the chosen role (`upsertMembership` into that one workspace); unchecking
 * removes it (`removeMembershipFromWorkspace`); changing a role of an existing membership re-upserts
 * it. Every change invalidates `['managed-members']` via `onChanged` and confirms with the app's
 * "Changes saved" toast; errors (incl. the backend's last-admin 400) surface inline.
 */
function EditWorkspaceAccess({
  member,
  workspaces,
  onChanged,
}: {
  member: ManagedMember;
  workspaces: ManageableWorkspace[];
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  // Intended role for workspaces the user is NOT yet a member of (used when their box is checked).
  const [draftRoles, setDraftRoles] = useState<Record<string, WorkspaceRole>>({});
  const roleFieldBase = useFieldId('wsaccess-role');
  const { confirm } = useDialogs();
  const toast = useToast();
  // On App Settings, leaving the tab or the page waits for a change still saving here (useSaveHold).
  const hold = useSaveHold();

  const membershipByTenant = new Map(member.memberships.map((m) => [m.tenantId, m.role]));

  const grant = useMutation({
    mutationFn: ({ tenantId, role }: { tenantId: string; role: WorkspaceRole }) =>
      api.upsertMembership({ email: member.user.email, role, tenantIds: [tenantId] }),
    onSuccess: () => {
      setError(null);
      onChanged();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not update workspace access.'),
  });

  const revoke = useMutation({
    mutationFn: (tenantId: string) =>
      api.removeMembershipFromWorkspace(member.user.id, tenantId),
    onSuccess: () => {
      setError(null);
      onChanged();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not remove workspace access.'),
  });

  const busy = grant.isPending || revoke.isPending;

  return (
    <div className="mt-3 space-y-2 border-t border-line-soft pt-3">
      <p className="text-xs font-medium text-ink-label">Brand Workspace access</p>
      {workspaces.length === 0 ? (
        <p className="text-sm text-ink-faint">No Brand Workspaces you manage.</p>
      ) : (
        <ul className="space-y-1.5">
          {workspaces.map((ws) => {
            const isMemberOf = membershipByTenant.has(ws.id);
            const role = isMemberOf
              ? membershipByTenant.get(ws.id)!
              : (draftRoles[ws.id] ?? WorkspaceRole.MEMBER);
            const roleId = `${roleFieldBase}-${ws.id}`;
            const rowBusy =
              (grant.isPending && grant.variables?.tenantId === ws.id) ||
              (revoke.isPending && revoke.variables === ws.id);
            return (
              <li
                key={ws.id}
                className="flex flex-wrap items-center gap-3 rounded-md border border-line px-3 py-2"
              >
                <label className="flex min-w-0 flex-1 items-center gap-2 text-sm text-ink-strong">
                  <input
                    type="checkbox"
                    checked={isMemberOf}
                    disabled={busy}
                    onChange={async () => {
                      setError(null);
                      if (isMemberOf) {
                        // Confirm removing access (a permission revoke) to guard against errant clicks.
                        if (
                          !(await confirm({
                            title: 'Remove access',
                            message: `Remove ${member.user.displayName || member.user.email}'s access to “${ws.name}”? They will lose access to this Brand Workspace immediately.`,
                            confirmLabel: 'Remove',
                            danger: true,
                          }))
                        ) {
                          return;
                        }
                        void hold(revoke.mutateAsync(ws.id));
                      } else {
                        void hold(grant.mutateAsync({ tenantId: ws.id, role }));
                      }
                    }}
                    className="focus-ring h-4 w-4 shrink-0 rounded border-line-input text-action"
                  />
                  <span className="truncate">{ws.name}</span>
                </label>
                <div className="w-44">
                  <label htmlFor={roleId} className="sr-only">
                    Role in {ws.name}
                  </label>
                  <RoleSelect
                    id={roleId}
                    value={role}
                    disabled={busy}
                    onChange={async (r) => {
                      setError(null);
                      if (isMemberOf) {
                        // Confirm a demotion (admin → member) — a permission revoke.
                        if (
                          role === WorkspaceRole.WORKSPACE_ADMIN &&
                          r === WorkspaceRole.MEMBER &&
                          !(await confirm({
                            title: 'Change role',
                            message: `Change ${member.user.displayName || member.user.email}'s role in “${ws.name}” to Member? They will lose Brand Workspace Admin rights here.`,
                            confirmLabel: 'Change role',
                          }))
                        ) {
                          return;
                        }
                        void hold(grant.mutateAsync({ tenantId: ws.id, role: r }));
                      } else {
                        setDraftRoles((prev) => ({ ...prev, [ws.id]: r }));
                      }
                    }}
                  />
                </div>
                {rowBusy && <span className="text-xs text-ink-faint">Saving…</span>}
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
    </div>
  );
}

/**
 * One user in the cross-workspace member view: name/email, a "Tenant Admin" badge, an "External"
 * badge, and a chip per workspace membership ("<workspace> · <Role>"). Admins additionally get a
 * "Edit Workspace Access" expander; a Tenant Admin also gets a per-user Tenant-Admin toggle.
 */
function ManagedMemberRow({
  member,
  workspaces,
  isAdmin,
  canManageTenantAdmin,
  isSelf,
  onChanged,
  onSelfChanged,
}: {
  member: ManagedMember;
  workspaces: ManageableWorkspace[];
  isAdmin: boolean;
  canManageTenantAdmin: boolean;
  isSelf: boolean;
  onChanged: () => void;
  onSelfChanged: () => void;
}) {
  const { user, isTenantAdmin, memberships } = member;
  // The install's billing contact — the only person who can change plan, seats or card on
  // /billing. Everyone else sees that page read-only.
  const isBillingAdmin = Boolean(user.isBillingAdmin);
  // A Workspace Admin (not a Tenant Admin) may not modify a Tenant Admin's workspace access.
  const lockedTenantAdmin = isTenantAdmin && !canManageTenantAdmin;
  const [editingAccess, setEditingAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { confirm } = useDialogs();
  const toast = useToast();
  const hold = useSaveHold();

  const name = user.displayName || user.email;

  const toggleTenantAdmin = useMutation({
    mutationFn: () => api.setTenantAdmin(user.id, !isTenantAdmin),
    onSuccess: () => {
      setError(null);
      onChanged();
      toast.show(CHANGES_SAVED);
      // Self-demotion changes our own gating — refresh the principal so admin-only affordances
      // update without a reload.
      if (isSelf) onSelfChanged();
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError ? err.message : 'Could not change Tenant Admin access.',
      ),
  });

  return (
    <li className="rounded-lg border border-line px-4 py-3">
      {/* Name + actions share the top row: actions are right-justified and inline with the name,
          consistent regardless of how many workspace badges a member has. Email + badges below. */}
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
          {name}
          {isSelf && <span className="font-normal text-ink-faint"> (you)</span>}
        </p>
        {isAdmin && (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              // Only a Tenant Admin may change another Tenant Admin's workspace access (the API
              // enforces this too); a Workspace Admin sees the control disabled rather than a 403.
              disabled={lockedTenantAdmin}
              title={
                lockedTenantAdmin
                  ? 'Only a Tenant Admin can change a Tenant Admin’s Brand Workspace access'
                  : undefined
              }
              onClick={() => {
                setError(null);
                setEditingAccess((v) => !v);
              }}
            >
              {editingAccess ? 'Close' : 'Edit Brand Workspace Access'}
              <ChevronDown
                className={`h-3.5 w-3.5 transition ${editingAccess ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </Button>
            {canManageTenantAdmin && (
              <Button
                variant="ghost"
                size="sm"
                loading={toggleTenantAdmin.isPending}
                onClick={async () => {
                  // Confirm a revoke (not a grant) to guard against errant clicks — with an
                  // extra-clear warning when revoking YOUR OWN admin (only another Tenant Admin
                  // can restore it).
                  if (isTenantAdmin) {
                    const msg = isSelf
                      ? 'Revoke your OWN Tenant Admin access? You will immediately lose install-wide admin rights across every Brand Workspace, and only another Tenant Admin can restore them.'
                      : `Revoke Tenant Admin from ${name}? They will lose install-wide admin rights across every Brand Workspace.`;
                    if (
                      !(await confirm({
                        title: 'Revoke Tenant Admin',
                        message: msg,
                        confirmLabel: 'Revoke',
                        danger: true,
                      }))
                    )
                      return;
                  }
                  void hold(toggleTenantAdmin.mutateAsync());
                }}
              >
                {isTenantAdmin ? 'Revoke Tenant Admin' : 'Make Tenant Admin'}
                {isTenantAdmin ? (
                  <ShieldOff className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                )}
              </Button>
            )}
            {/* "Make Billing Admin" used to sit here. Removed on the owner's instruction: billing
                access is managed in ONE place, /billing-admin. Who may change things on /billing is
                now simply a Tenant Admin (billing.controller.ts), so this per-user flag no longer
                gates anything and designating it here would be a control with no effect. The badge
                below still renders for rows that carry the flag, so existing data reads honestly. */}
          </div>
        )}
      </div>

      <p className="truncate text-xs text-ink-muted">{user.email}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        {isTenantAdmin && <Badge tone="brandSolid">Tenant Admin</Badge>}
        {/* A distinct tone from Tenant Admin: the two roles are independent, and a green badge
            beside a brand one reads as "also holds this" rather than a variant of it. */}
        {isBillingAdmin && <Badge tone="green">Billing Admin</Badge>}
        {user.isExternalStakeholder && <Badge tone="amber">External</Badge>}
        {memberships.length === 0 ? (
          <span className="text-xs text-ink-faint">No Brand Workspace access</span>
        ) : (
          memberships.map((m) => (
            <Badge
              key={m.tenantId}
              tone={m.role === WorkspaceRole.WORKSPACE_ADMIN ? 'brand' : 'neutral'}
            >
              {m.tenantName} · {workspaceRoleLabel(m.role)}
            </Badge>
          ))
        )}
      </div>

      {isAdmin && editingAccess && !lockedTenantAdmin && (
        <EditWorkspaceAccess member={member} workspaces={workspaces} onChanged={onChanged} />
      )}

      {error && <p className="mt-2 text-sm text-red-600 dark:text-error-dark">{error}</p>}
    </li>
  );
}

/**
 * Add a member by giving an email address → an access role → one or more Brand Workspaces.
 *
 * The address can be **typed**, or picked from a registered Microsoft 365 group when this install
 * has any. Typing is the path that always works; the group picker is a convenience that depends on
 * Microsoft Graph app roles (`['entra-group-members', tenantKey, groupId]`).
 *
 * Typing used to be impossible: adding required a group selection, so an install with no registered
 * groups could not add anyone at all. That is every customer install — they federate their own
 * directory (Google Workspace for RescueMeds) and deliberately hold no Graph roles, because reading
 * a directory there would expose OURS, not theirs. So the group field is hidden entirely when there
 * are no groups, and leaving it unselected means "type an address".
 *
 * Either way the write is the same `upsertMembership`, which creates the row with no `entraOid`;
 * `resolveOnce` step 2 links it to the real identity on that person's first sign-in.
 *
 * The role choices are the per-workspace roles (Brand Workspace Admin | Member) plus, when the
 * caller is a Tenant Admin (`canAssignTenantAdmin`), the install-wide **Tenant Admin**. Choosing
 * Tenant Admin adds the member to the selected Brand Workspaces (as Member) and then grants the
 * install-wide flag — mirroring the two operations the API exposes.
 */
type AddRole = 'TENANT_ADMIN' | WorkspaceRole;

function AddMemberRow({
  groups,
  workspaces,
  tenantKey,
  canAssignTenantAdmin,
  onAdded,
}: {
  groups: RegisteredEntraGroup[];
  workspaces: ManageableWorkspace[];
  tenantKey: string;
  canAssignTenantAdmin: boolean;
  onAdded: () => void;
}) {
  const groupId = useFieldId('add-group');
  const emailFieldId = useFieldId('add-email');
  const roleId = useFieldId('add-role');
  const wsFieldBase = useFieldId('add-workspaces');

  const [selectedGroup, setSelectedGroup] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AddRole>(WorkspaceRole.MEMBER);
  const [tenantIds, setTenantIds] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const hold = useSaveHold();

  const membersQuery = useQuery({
    queryKey: ['entra-group-members', tenantKey, selectedGroup],
    queryFn: ({ signal }) => api.entraGroupMembers(selectedGroup, signal),
    enabled: tenantKey !== '' && selectedGroup !== '',
  });
  const groupMembers = membersQuery.data ?? [];

  const add = useMutation({
    mutationFn: async () => {
      // Tenant Admin isn't a per-workspace role: add the membership(s), then grant the install-wide
      // flag (which already makes them admin everywhere). Add them as Brand Workspace Admin — NOT
      // Member — so the grant can't be rejected by the workspace last-admin guard (it only fires
      // when demoting to a non-admin role) and an existing Workspace Admin isn't silently demoted
      // (which would strand them as a plain Member if Tenant Admin is later revoked). Both API
      // actions are Tenant-Admin-only, enforced server-side.
      const wsRole: WorkspaceRole =
        role === 'TENANT_ADMIN' ? WorkspaceRole.WORKSPACE_ADMIN : role;
      const member = await api.upsertMembership({ email, role: wsRole, tenantIds: [...tenantIds] });
      if (role === 'TENANT_ADMIN') {
        await api.setTenantAdmin(member.user.id, true);
      }
      return member;
    },
    onSuccess: () => {
      setError(null);
      // Reset / auto-collapse the picked member so the admin can add another.
      setEmail('');
      onAdded();
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not add this member.'),
  });

  const onSelectGroup = (id: string) => {
    setError(null);
    setSelectedGroup(id);
    setEmail(''); // the member list changes with the group
  };

  const toggleWorkspace = (id: string) => {
    setError(null);
    setTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** No group chosen (or none to choose) → the address is typed rather than picked. */
  const typingEmail = selectedGroup === '';
  // Enough to catch a slip, not an attempt at RFC 5322: the server's @IsEmail() is the authority.
  const typedEmailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  // No longer requires a group. Requiring one meant an install with no registered groups could
  // not add a member at all, which is the state every customer install is in by design.
  const canAdd =
    email !== '' &&
    (typingEmail ? typedEmailLooksValid : true) &&
    tenantIds.size > 0 &&
    !add.isPending;

  // Adding an EXISTING member to another workspace takes no new seat, so the cap only blocks
  // someone who is not already on the roster — the same rule the server applies.
  const usage = useSeatUsage();
  const seatHolders = useSeatHolders();
  const alreadyHoldsSeat = email !== '' && seatHolders.emails.has(email.toLowerCase());
  const remaining = seatsRemaining(usage.data);
  // Only block when we KNOW the person would take a new seat. While the roster is still loading,
  // leave it to the server — a wrong refusal here is worse than a refusal one round trip later.
  const seatsFull =
    seatHolders.loaded && !alreadyHoldsSeat && remaining !== null && remaining <= 0;

  const emailPlaceholder =
    selectedGroup === ''
      ? 'Choose a group first…'
      : membersQuery.isLoading
        ? 'Loading members…'
        : membersQuery.isError
          ? 'Could not load members'
          : groupMembers.length === 0
            ? 'This group has no members'
            : 'Select a member…';

  return (
    <div className="space-y-4">
      <div
        className={`grid gap-4 sm:grid-cols-2 ${groups.length > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
      >
        {/* Hidden entirely when this install has no registered groups — an empty picker that
            can never be filled reads as a broken form rather than an absent convenience. */}
        {groups.length > 0 && (
          <FormField
            label="Microsoft 365 group"
            htmlFor={groupId}
            hint="Optional — leave unselected to type an address."
          >
            <Select
              id={groupId}
              value={selectedGroup}
              onChange={(e) => onSelectGroup(e.target.value)}
            >
              <option value="">Type an address instead…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField label={typingEmail ? 'Email address' : 'Member'} htmlFor={emailFieldId}>
          {typingEmail ? (
            <Input
              id={emailFieldId}
              type="email"
              autoComplete="off"
              value={email}
              placeholder="colleague@example.com"
              onChange={(e) => {
                setError(null);
                setEmail(e.target.value.trim());
              }}
            />
          ) : (
            <Select
              id={emailFieldId}
              value={email}
              disabled={membersQuery.isLoading || groupMembers.length === 0}
              onChange={(e) => {
                setError(null);
                setEmail(e.target.value);
              }}
            >
              <option value="">{emailPlaceholder}</option>
              {groupMembers.map((m) => (
                <option key={m.oid} value={m.email}>
                  {m.displayName} ({m.email})
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormField label="Role" htmlFor={roleId}>
          <Select
            id={roleId}
            value={role}
            onChange={(e) => {
              setError(null);
              setRole(e.target.value as AddRole);
            }}
          >
            {/* Only a Tenant Admin may grant the install-wide Tenant Admin role. */}
            {canAssignTenantAdmin && <option value="TENANT_ADMIN">Tenant Admin</option>}
            <option value={WorkspaceRole.WORKSPACE_ADMIN}>Brand Workspace Admin</option>
            <option value={WorkspaceRole.MEMBER}>Member</option>
          </Select>
        </FormField>

        <div>
          <span className="mb-1 block text-sm font-medium text-ink-strong">Brand Workspace</span>
          <details className="group relative">
            <summary className="focus-ring flex cursor-pointer list-none items-center justify-between rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink [&::-webkit-details-marker]:hidden">
              <span className="truncate">
                {tenantIds.size === 0 ? 'Select Brand Workspaces…' : `${tenantIds.size} selected`}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-ink-faint transition group-open:rotate-180"
                aria-hidden
              />
            </summary>
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-line bg-card p-1 shadow-lg">
              {workspaces.length === 0 ? (
                <p className="px-3 py-2 text-sm text-ink-faint">No Brand Workspaces you manage.</p>
              ) : (
                workspaces.map((ws) => {
                  const id = `${wsFieldBase}-${ws.id}`;
                  return (
                    <label
                      key={ws.id}
                      htmlFor={id}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-ink-strong hover:bg-hover"
                    >
                      <input
                        id={id}
                        type="checkbox"
                        checked={tenantIds.has(ws.id)}
                        onChange={() => toggleWorkspace(ws.id)}
                        className="focus-ring h-4 w-4 shrink-0 rounded border-line-input text-action"
                      />
                      <span className="truncate">{ws.name}</span>
                    </label>
                  );
                })
              )}
            </div>
          </details>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          loading={add.isPending}
          // Stopped here as well as server-side: the API refuses it anyway, but letting someone
          // pick a person and a role first only to be told there was never room reads as a bug.
          disabled={!canAdd || seatsFull}
          onClick={() => {
            if (!canAdd || seatsFull) return;
            setError(null);
            void hold(add.mutateAsync());
          }}
        >
          <UserPlus className="h-4 w-4" aria-hidden /> Add member
        </Button>
        {seatsFull && (
          <span className="text-sm text-red-600 dark:text-error-dark">
            All {usage.data?.cap} seats are in use. Remove a member, or raise the cap on the Billing
            page.
          </span>
        )}
        {error && <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>}
      </div>
    </div>
  );
}

/** What the invite form says once an approver is saved: with the portal's link, or without it. */
export const APPROVER_INVITED_WITH_LINK =
  'Invited. Tick them as an approver for that Brand Workspace, then send them this link:';
export const APPROVER_INVITED_PORTAL_OFF =
  'Invited. Tick them as an approver for that Brand Workspace. The approval page is switched off on this site for now.';

/**
 * "Invite an external approver": the only way someone outside the organization gets into the
 * approval portal. Email, an optional name and ONE Brand Workspace the caller manages; the API
 * checks that, and refuses an address that already has an account. Its answer says whether this
 * site's portal is switched on: only then is the portal link offered - with it off, the link would
 * lead to a page that only says so.
 */
function InviteApproverRow({
  workspaces,
  onInvited,
}: {
  workspaces: ManageableWorkspace[];
  onInvited: () => void;
}) {
  const emailId = useFieldId('invite-email');
  const nameId = useFieldId('invite-name');
  const workspaceId = useFieldId('invite-workspace');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The last invite that went through: whose portal link to show, and whether to show one at all.
  const [invited, setInvited] = useState<{ tenantId: string; portalEnabled: boolean } | null>(null);
  const toast = useToast();
  const hold = useSaveHold();

  const invite = useMutation({
    mutationFn: () =>
      api.inviteExternalApprover({
        email: email.trim(),
        displayName: name.trim() || undefined,
        tenantId,
      }),
    onSuccess: (result) => {
      setError(null);
      setInvited({ tenantId: result.membership.tenantId, portalEnabled: result.portalEnabled });
      // Ready for the next person; the workspace stays, as several approvers often share one.
      setEmail('');
      setName('');
      onInvited();
      // The approver is saved: the toast confirms it, as for every save here, and the note above keeps
      // what to do next.
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not invite this approver.'),
  });

  const canInvite = email.trim() !== '' && tenantId !== '' && !invite.isPending;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FormField label="Email" htmlFor={emailId}>
          <Input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => {
              setError(null);
              setEmail(e.target.value);
            }}
          />
        </FormField>

        <FormField label="Name" htmlFor={nameId}>
          <Input
            id={nameId}
            value={name}
            maxLength={120}
            onChange={(e) => {
              setError(null);
              setName(e.target.value);
            }}
          />
        </FormField>

        <FormField label="Brand Workspace" htmlFor={workspaceId}>
          <Select
            id={workspaceId}
            value={tenantId}
            onChange={(e) => {
              setError(null);
              setTenantId(e.target.value);
            }}
          >
            <option value="">Select a Brand Workspace...</option>
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </Select>
        </FormField>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          loading={invite.isPending}
          disabled={!canInvite}
          onClick={() => {
            if (!canInvite) return;
            setError(null);
            setInvited(null);
            void hold(invite.mutateAsync());
          }}
        >
          <UserPlus className="h-4 w-4" aria-hidden /> Invite approver
        </Button>
        {error ? (
          <span className="text-sm text-red-600 dark:text-error-dark">{error}</span>
        ) : (
          invited && (
            <div className="w-full text-sm text-ink-strong" role="status">
              <p>{invited.portalEnabled ? APPROVER_INVITED_WITH_LINK : APPROVER_INVITED_PORTAL_OFF}</p>
              {invited.portalEnabled && (
                <CopyableUrl value={`${window.location.origin}/approve?tenant=${invited.tenantId}`} />
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Cross-workspace member management. Lists every user the caller manages (`getManagedMembers`) —
 * one row per user with their Tenant-Admin/External badges and a chip per workspace membership —
 * and, for workspace admins, an add-member flow (pick a Microsoft 365 group → member → role →
 * workspaces) plus a per-user "Edit Workspace Access" editor and a Tenant-Admin toggle, and an
 * "Invite an external approver" form for people outside the organization.
 *
 * Writes invalidate `['managed-members']` (this list) and `['members', tenantKey]` (the
 * active-workspace list feeding the approver picker). Non-admins see a read-only list; the API is
 * the authoritative gate.
 */
export function MembersCard() {
  const tenantKey = useTenantKey();
  const { principal, refresh } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = useIsWorkspaceAdmin();
  const isTenantAdmin = useIsTenantAdmin();
  const currentUserId = principal?.user.id;

  const membersQuery = useQuery({
    queryKey: ['managed-members'],
    queryFn: ({ signal }) => api.getManagedMembers(signal),
  });
  const members = membersQuery.data ?? [];

  // A caller who manages ≥1 workspace can manage members. getManagedMembers always includes the
  // caller when they administer anything, so a non-empty result ⇒ manager; a plain member gets [].
  // Prefer this over the active-only useIsWorkspaceAdmin() so a Workspace Admin whose managed
  // workspace isn't the active one still sees the manage UI (the API remains the authoritative gate).
  const canManage = isAdmin || members.length > 0;

  const workspacesQuery = useQuery({
    queryKey: ['manageable-workspaces'],
    queryFn: ({ signal }) => api.getManageableWorkspaces(signal),
    enabled: canManage,
  });
  const workspaces = workspacesQuery.data ?? [];

  const groupsQuery = useQuery({
    // Install-wide registered groups (shared across Brand Workspaces) — key omits tenantKey.
    queryKey: ['entra-groups'],
    queryFn: ({ signal }) => api.listEntraGroups(signal),
    enabled: canManage && tenantKey !== '',
  });
  const groups = groupsQuery.data ?? [];

  // Read-only fallback for a caller who manages nothing: the active workspace's roster
  // (any member may GET /users). Only fetched once we know the caller can't manage members.
  const rosterQuery = useQuery({
    queryKey: ['members', tenantKey],
    queryFn: ({ signal }) => api.listMembers(signal),
    enabled: !canManage && tenantKey !== '',
  });
  const roster = rosterQuery.data ?? [];

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['managed-members'] });
    // Keep the active-workspace member list (approver picker) in sync when the active workspace
    // is one of those changed.
    void queryClient.invalidateQueries({ queryKey: ['members', tenantKey] });
    // The headcount moved, so the counter and the cap gate must move with it.
    void queryClient.invalidateQueries({ queryKey: ['seat-usage'] });
    void queryClient.invalidateQueries({ queryKey: ['billing-members'] });
  };

  const seatUsage = useSeatUsage();

  return (
    <Card>
      <CardHeader
        title="Members"
        description="Manage who can collaborate across your Brand Workspaces and set their access role."
        action={seatUsage.data ? <SeatCounter usage={seatUsage.data} /> : undefined}
      />
      <CardBody className="space-y-5">
        {/* Current members */}
        {membersQuery.isLoading ? (
          <p className="text-sm text-ink-faint">Loading members…</p>
        ) : membersQuery.isError ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load members.</p>
        ) : canManage ? (
          <>
            {members.length === 0 && (
              <p className="text-sm text-ink-muted">
                No members yet. Add teammates from a Microsoft 365 group below.
              </p>
            )}
            <ul className="space-y-2">
              {members.map((m) => (
                <ManagedMemberRow
                  key={m.user.id}
                  member={m}
                  workspaces={workspaces}
                  isAdmin={canManage}
                  canManageTenantAdmin={isTenantAdmin}
                  isSelf={m.user.id === currentUserId}
                  onChanged={invalidate}
                  onSelfChanged={() => void refresh()}
                />
              ))}
            </ul>
          </>
        ) : (
          // Caller manages nothing: a read-only roster of the ACTIVE workspace (not the
          // misleading "No members yet" empty-state — there ARE members, they just can't manage).
          <div className="space-y-3">
            <p className="text-sm text-ink-muted">You have read-only access to this member list.</p>
            {rosterQuery.isLoading ? (
              <p className="text-sm text-ink-faint">Loading members…</p>
            ) : rosterQuery.isError ? (
              <p className="text-sm text-red-600 dark:text-error-dark">Could not load members.</p>
            ) : roster.length === 0 ? (
              <p className="text-sm text-ink-muted">No members in this Brand Workspace yet.</p>
            ) : (
              <ul className="space-y-2">
                {roster.map(({ user, membership }) => (
                  <li key={user.id} className="rounded-lg border border-line px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {user.displayName || user.email}
                        </p>
                        <p className="truncate text-xs text-ink-muted">{user.email}</p>
                        {user.isExternalStakeholder && (
                          <div className="mt-1.5">
                            <Badge tone="amber">External</Badge>
                          </div>
                        )}
                      </div>
                      <Badge
                        tone={
                          membership.role === WorkspaceRole.WORKSPACE_ADMIN ? 'brand' : 'neutral'
                        }
                      >
                        {workspaceRoleLabel(membership.role)}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Add a member (managers only).
            The form is ALWAYS rendered. It used to be replaced by "No Microsoft 365 groups
            registered yet" whenever none were, which left every customer install with no way to
            add anyone at all — they federate their own directory and hold no Graph roles on
            purpose, so that message was advice they could never act on. Typing an address needs
            no group; the group picker appears inside the row only when there is one to offer. */}
        {canManage && (
          <div className="space-y-4 border-t border-line-soft pt-5">
            <p className="text-sm font-medium text-ink-strong">Add a member</p>
            {groupsQuery.isLoading ? (
              <p className="text-sm text-ink-faint">Loading groups…</p>
            ) : (
              <>
                {groupsQuery.isError && (
                  <p className="text-sm text-ink-muted">
                    Could not load Microsoft 365 groups — add a member by typing their email
                    address.
                  </p>
                )}
                <AddMemberRow
                  groups={groupsQuery.isError ? [] : groups}
                  workspaces={workspaces}
                  tenantKey={tenantKey}
                  canAssignTenantAdmin={isTenantAdmin}
                  onAdded={invalidate}
                />
              </>
            )}
          </div>
        )}

        {/* Invite an approver from outside the organization (managers only) */}
        {canManage && (
          <div className="space-y-4 border-t border-line-soft pt-5">
            <div>
              <p className="text-sm font-medium text-ink-strong">Invite an external approver</p>
              <p className="mt-1 text-sm text-ink-muted">
                For clients and other people outside your organization. They sign in at the approval
                portal with a code and can only review campaigns sent for approval. They do not use a
                seat.
              </p>
            </div>
            <InviteApproverRow workspaces={workspaces} onInvited={invalidate} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
